import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";
import { useAuth } from "./useAuth";

export type NotificationType = 
  | 'new_content' 
  | 'new_game' 
  | 'achievement' 
  | 'community_post' 
  | 'comment' 
  | 'message' 
  | 'system' 
  | 'moderation';

export type Notification = {
  id: string;
  type: NotificationType;
  title: string;
  message: string;
  link?: string;
  reference_id?: string;
  read: boolean;
  created_at: string;
  deleted_at?: string;
  metadata?: {
    achievement_id?: string;
    post_id?: string;
    reaction_id?: string;
    reaction_emoji?: string;
    user_name?: string;
    reason?: string;
    action?: string;
  };
};

export const useNotifications = () => {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!user?.id) return;

    const translateReason = (reason: string) => {
      const reasonTranslations: { [key: string]: string } = {
        'spam': 'Spam',
        'inappropriate': 'Contenido inapropiado',
        'harassment': 'Acoso',
        'misinformation': 'Información falsa',
        'other': 'Otro motivo'
      };
      return reasonTranslations[reason.toLowerCase()] || reason;
    };

    const channel = supabase
      .channel('notifications')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const notification = payload.new as Notification;
          
          let icon = '📬';
          let title = 'Nueva notificación';
          let description = notification.message;

          switch (notification.type) {
            case 'comment':
              icon = '💬';
              title = 'Nuevo comentario';
              description = `${notification.metadata?.user_name || 'Alguien'} ha comentado en tu publicación`;
              break;
            case 'system':
              if (notification.metadata?.action === 'moderation') {
                icon = '⚠️';
                if (notification.metadata?.reason) {
                  description = `Se ha revisado tu publicación por: ${translateReason(notification.metadata.reason)}`;
                }
              } else {
                icon = '🔔';
                description = notification.message;
              }
              break;
            default:
              // Mantener los valores por defecto
              break;
          }

          window.dispatchEvent(new CustomEvent('new-notification', { 
            detail: { 
              ...notification, 
              icon,
              title,
              description
            } 
          }));
          
          // Reproducir sonido de notificación
          const audio = new Audio('/sounds/notification.mp3');
          audio.play().catch(() => {}); // Ignorar errores si el navegador bloquea el audio
          
          queryClient.invalidateQueries(['notifications']);
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [user?.id, queryClient]);

  // Obtener notificaciones
  const { data: notifications = [], ...queryResults } = useQuery({
    queryKey: ['notifications', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];

      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .is('deleted_at', null)
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return data as Notification[];
    },
    enabled: !!user?.id,
    refetchInterval: 30000, // Refrescar cada 30 segundos
    staleTime: 0, // Considerar los datos como obsoletos inmediatamente
    cacheTime: 5 * 60 * 1000, // Mantener en caché por 5 minutos
    refetchOnMount: true, // Refrescar cuando el componente se monta
    refetchOnWindowFocus: true // Refrescar cuando la ventana obtiene el foco
  });

  // Calcular notificaciones no leídas
  const unreadCount = notifications.filter(n => !n.read).length;

  // Marcar como leída
  const markAsRead = async (notificationId: string) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('id', notificationId)
        .eq('user_id', user.id);

      if (error) throw error;

      queryClient.setQueryData(['notifications'], (old: Notification[] | undefined) =>
        old?.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Marcar todas como leídas
  const markAllAsRead = async () => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read: true })
        .eq('user_id', user.id)
        .is('read', false);

      if (error) throw error;

      queryClient.setQueryData(['notifications'], (old: Notification[] | undefined) =>
        old?.map(n => ({ ...n, read: true }))
      );
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  // Eliminar notificación
  const deleteNotification = async (notificationId: string) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('notifications')
        .update({ deleted_at: new Date().toISOString() })
        .eq('id', notificationId)
        .eq('user_id', user.id);

      if (error) throw error;

      queryClient.setQueryData(['notifications'], (old: Notification[] | undefined) =>
        old?.filter(n => n.id !== notificationId)
      );
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  return {
    notifications,
    unreadCount,
    ...queryResults,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  };
};
